<template>
  <BaseContainer class="p-6">
    <BaseSection>
      <div class="flex gap-4">
        <div class="flex-1 text-sm">
          <p>Vous pouvez configurer les notifications afin de les adapter à votre poste.</p>
          L’activation/désactivation des notifications se gère depuis l’application
          Apodis PRO > Mon Compte > Gestion des notifications.
        </div>
        <div class="flex items-start">
          <ShowAdvancedOptionsMenu @toggle-advanced="toggleAdvancedOptions" />
        </div>
      </div>
    </BaseSection>

    <BaseSection>
      <BaseCard>
        <BaseCardBody>
          <h3 class="mb-1 font-bold text-sm">
            Emplacement de la notification
          </h3>

          <p class="mb-4 text-gray-500 text-sm">
            Choisissez un emplacement prédéfini ci-dessous.
            <template v-if="canDrag && !preference.notification.dragDisabled">
              Vous pouvez aussi déplacer directement la notification en faisant
              glisser son onglet vers le bord gauche ou droit de votre écran.
            </template>
          </p>

          <p
            v-if="preference.notification.position === 'custom'"
            class="mb-4 text-primary text-sm font-medium"
          >
            Position personnalisée définie par glisser-déposer.
            Sélectionnez une option ci-dessous pour revenir à un emplacement prédéfini.
          </p>

          <div class="flex flex-1 flex-row space-x-4">
            <NotificationLocationRadioButton
              v-model="preference.notification.position"
              :value="'bottom-edge'"
              label="Tout en bas"
            />
            <NotificationLocationRadioButton
              v-model="preference.notification.position"
              :value="'bottom-right'"
              label="En bas à droite"
            />
            <NotificationLocationRadioButton
              v-model="preference.notification.position"
              :value="'middle-right'"
              label="Au milieu à droite"
            />
            <NotificationLocationRadioButton
              v-model="preference.notification.position"
              :value="'top-right'"
              label="En haut à droite"
            />
            <NotificationLocationRadioButton
              v-model="preference.notification.position"
              :value="'top-edge'"
              label="Tout en haut"
            />
          </div>
        </BaseCardBody>
      </BaseCard>

      <BaseCard v-if="canDrag">
        <BaseCardBody>
          <h3 class="mb-2 font-bold text-sm">
            Déplacement par glisser-déposer
          </h3>

          <p class="mb-4 text-gray-500 text-sm">
            Autorise le déplacement de la notification en faisant glisser sa poignée
            vers le bord gauche ou droit de l'écran.
          </p>

          <div class="space-y-3">
            <BaseRadio
              v-model="preference.notification.dragDisabled"
              :value="false"
              label="Oui"
            />
            <BaseRadio
              v-model="preference.notification.dragDisabled"
              :value="true"
              label="Non"
            />
          </div>
        </BaseCardBody>
      </BaseCard>

      <BaseCard>
        <BaseCardBody>
          <h3 class="mb-2 font-bold text-sm">
            Temps d’affichage de la notification
          </h3>

          <div class="flex flex-col space-y-2 md:flex-row md:space-y-0">
            <div class="space-y-2">
              <BaseRadio
                v-model="preference.notification.time"
                :value="5000"
                label="5 secondes"
              />
              <BaseRadio
                v-model="preference.notification.time"
                :value="15000"
                label="15 secondes"
              />
            </div>
            <div class="space-y-2 md:ml-10">
              <BaseRadio
                v-model="preference.notification.time"
                :value="30000"
                label="30 secondes"
              />
              <BaseRadio
                v-model="preference.notification.time"
                :value="60000"
                label="1 minute"
              />
            </div>
            <div class="space-y-2 md:ml-10">
              <BaseRadio
                v-model="preference.notification.time"
                :value="0"
                label="Permanent"
              />
            </div>
          </div>
        </BaseCardBody>
      </BaseCard>

      <BaseCard>
        <BaseCardBody>
          <h3 class="mb-2 font-bold text-sm">
            Apparence de la notification
          </h3>
          <div class="flex flex-row space-x-10">
            <NotificationLayoutRadioButton
              v-model="preference.notification.layout"
              :value="'simple'"
              label="Simple : affiche les programmes concernant le patient détecté"
              class="w-1/2"
            />
            <NotificationLayoutRadioButton
              v-model="preference.notification.layout"
              :value="'advanced'"
              label="Avancé : affiche les programmes du patient détecté avec l'action à effectuer"
              class="w-1/2"
            />
          </div>
        </BaseCardBody>
      </BaseCard>

      <BaseCard>
        <BaseCardBody>
          <h3 class="mb-2 font-bold text-sm">
            Afficher la fiche patient au survol de la notification
          </h3>
          <div class="space-y-3">
            <BaseRadio
              v-model="preference.notification.supportProgramHoverFeatureEnabled"
              :value="true"
              label="Oui"
            />
            <BaseRadio
              v-model="preference.notification.supportProgramHoverFeatureEnabled"
              :value="false"
              label="Non"
            />
          </div>
        </BaseCardBody>
      </BaseCard>

      <BaseCard v-if="liveWorks || live.installMode === 'install'">
        <BaseCardBody>
          <h3 class="mb-1 font-bold text-sm">
            Détection du patient au comptoir
          </h3>
          <p
            v-if="liveWorks"
            class="mb-4 text-gray-500 text-sm"
          >
            La détection rapide fonctionne sur ce poste : le patient est reconnu dès que vous ouvrez sa fiche
            dans votre logiciel. Vous pouvez revenir à la lecture de l'écran si besoin.
          </p>
          <p
            v-else
            class="mb-4 text-gray-500 text-sm"
          >
            Avec la détection rapide, le patient est reconnu dès que vous ouvrez sa fiche dans
            {{ live.lgoName }}. Elle s'active sur ce poste avec une autorisation de Windows.
          </p>
          <div class="flex flex-row space-x-10">
            <BaseRadio
              v-model="detectionChoice"
              value="live"
              label="Détection rapide (recommandé)"
            />
            <BaseRadio
              v-model="detectionChoice"
              value="ocr"
              label="Lecture de l'écran"
            />
          </div>
          <LiveActivationDialog
            v-model:open="activationOpen"
            :activating="live.activating"
            :failed="live.failed"
            @activate="activateLive"
            @later="activationOpen = false"
          />
        </BaseCardBody>
      </BaseCard>

      <BaseCard v-if="showAdvanced && hasCardlessDetection">
        <BaseCardBody>
          <h3 class="mb-2 font-bold text-sm">
            Utiliser l'autocalibration
          </h3>
          <div class="flex flex-row space-x-10">
            <BaseRadio
              v-model="preference.autocalibration"
              :value="true"
              label="Oui"
            />
            <BaseRadio
              v-model="preference.autocalibration"
              :value="false"
              label="Non"
            />
          </div>
        </BaseCardBody>
      </BaseCard>
    </BaseSection>
  </BaseContainer>
</template>

<script lang="ts">
import {defineComponent, reactive, toRaw} from "vue";
import BaseCard from '@/core/components/base/BaseCard.vue';
import BaseRadio from '@/core/components/base/BaseRadio.vue';
import BaseSection from '@/core/components/base/BaseSection.vue';
import BaseCardBody from '@/core/components/base/BaseCardBody.vue';
import BaseContainer from '@/core/components/base/BaseContainer.vue';
import NotificationLocationRadioButton from '@/settings/components/NotificationLocationRadioButton.vue';
import NotificationLayoutRadioButton from '@/settings/components/NotificationLayoutRadioButton.vue';
import {storageService} from '@/container';
import {NotificationsSettings, UserSettings} from "@/settings/interfaces";
import {supportsLiveDetectionMode, supportsWindowPlacement} from "@/core/services/shellCapabilities";
import ShowAdvancedOptionsMenu from "@/settings/components/ShowAdvancedOptionsMenu.vue";
import {NOTIFICATION_PLACEMENT_CHANNEL} from "@/notification/services/notificationWindowPlacement";
import useLiveActivation from "@/live/composables/useLiveActivation";
import LiveActivationDialog from "@/live/components/LiveActivationDialog.vue";

/** Placements dropped in the notification window, while this screen is mounted. */
let placementChannel: BroadcastChannel | null = null;

export default defineComponent({
    components: {
        ShowAdvancedOptionsMenu,
        NotificationLayoutRadioButton,
        NotificationLocationRadioButton,
        BaseCard,
        BaseRadio,
        BaseSection,
        BaseCardBody,
        BaseContainer,
        LiveActivationDialog
    },

    setup() {
        return {live: reactive(useLiveActivation())};
    },

    data() {
        return {
            preference: {
                notification: {
                    time: 0,
                    position: '',
                    layout: '',
                    supportProgramHoverFeatureEnabled: false,
                } as NotificationsSettings,
                useCardlessDetection: true,
                autocalibration: true,
            } as UserSettings,
            showAdvanced: false as boolean,
            lgoId: null as number | null,
            skipNextPersist: false as boolean,
            /** Apodis Live works on this workstation: the service sees the LGO's traffic, or was just installed. */
            liveWorks: false as boolean,
            /**
             * The detection chosen. Where Apodis Live is absent the OCR runs,
             * and choosing the rapid detection opens its activation instead
             * of being saved.
             */
            detectionChoice: 'ocr' as 'live' | 'ocr',
            activationOpen: false as boolean,
        };
    },

    computed: {
        hasCardlessDetection(): boolean {
            return !!this.lgoId;
        },
        /** Drag & drop repositioning needs the shell placement primitives. */
        canDrag(): boolean {
            return supportsWindowPlacement();
        }
    },

    watch: {
        detectionChoice(choice: 'live' | 'ocr') {
            if (this.liveWorks) {
                this.preference.detectionMode = choice;
            } else if (choice === 'live') {
                this.activationOpen = true;
            }
        },

        /** The activation closed without installing: back to the OCR, which runs. */
        activationOpen(open: boolean) {
            if (!open && !this.liveWorks) {
                this.detectionChoice = 'ocr';
            }
        },

        preference: {
            deep: true,

            async handler(value) {
                if (this.skipNextPersist) {
                    this.skipNextPersist = false;
                    return;
                }

                await storageService().store('userSettings', toRaw(value));
                this.preview();
            }
        }
    },

    async created() {
        this.lgoId = Number(await storageService().read('lgoId'));
    },

    async mounted() {
        const preference = await storageService().read('userSettings') as UserSettings;

        // Settings saved before this key have none: normalize before binding so
        // a radio is pre-selected. Drag & drop is on unless the pharmacist opted out.
        preference.notification.dragDisabled = preference.notification.dragDisabled ?? false;

        // Where Apodis Live works, it is the default detection; the choice is
        // offered only there.
        const liveStatus = supportsLiveDetectionMode()
            ? await window.ipcRenderer.getLivePatientWatchStatus?.().catch(() => null)
            : null;

        this.liveWorks = (liveStatus?.lgos?.length ?? 0) > 0;

        if (this.liveWorks) {
            preference.detectionMode = preference.detectionMode ?? 'live';
            this.detectionChoice = preference.detectionMode;
        }

        this.preference = preference;

        // Refresh the local copy when the notification window is repositioned by drag & drop,
        // so the next save does not overwrite the new placement with stale values.
        placementChannel = new BroadcastChannel(NOTIFICATION_PLACEMENT_CHANNEL);
        placementChannel.onmessage = (event: MessageEvent<UserSettings>) => this.handlePlacementChanged(event.data);
    },

    beforeUnmount() {
        placementChannel?.close();
        placementChannel = null;
        window.ipcRenderer.send('hide-notification');
    },

    methods: {
        preview() {
            window.ipcRenderer.send('preview-detection');
        },
        handlePlacementChanged(settings: UserSettings) {
            this.skipNextPersist = true;
            this.preference = settings;
        },
        toggleAdvancedOptions(show: boolean) {
            this.showAdvanced = show;
        },
        /** Install Apodis Live from the settings: once installed, the rapid detection is the one chosen. */
        async activateLive() {
            const result = await this.live.activate();

            if (result.status === 'installed') {
                this.liveWorks = true;
                this.preference.detectionMode = 'live';
            }

            if (result.status !== 'failed') {
                this.activationOpen = false;
            }
        }
    }
});
</script>
